import {
  BarChart3,
  Bell,
  Building2,
  FilePlus2,
  Landmark,
  Menu,
  MessageCircle,
  Receipt,
  Rss,
  Search,
  User as UserIcon,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import React, { useState } from "react";
import SidebarItem from "@/components/Sidebar/SidebarItem";
import { SidebarCollapseToggle } from "@/components/Sidebar/sidebar-collapse-toggle";
import { useSidebarCollapsed } from "@/components/Sidebar/use-sidebar-collapsed";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import type { User } from "@/common/types/user";
import { AdminUnauthorized } from "@/components/AdminUnauthorized";
import { usePermissions, Permission } from "@/hooks/usePermissions";

export const description =
  "A products dashboard with a sidebar navigation and a main content area. The dashboard has a header with a search input and a user menu. The sidebar has a logo, navigation links, and a card with a call to action. The main content area shows an empty state with a call to action.";

type SideItem = {
  icon: React.ReactNode;
  label: string;
  route: string;
  adminOnly?: boolean;
  permission?: Permission;
};

const sideItems: SideItem[] = [
  {
    icon: <BarChart3 />,
    label: "Dashboard",
    route: "/admin/dashboard",
    adminOnly: true,
  },
  {
    icon: <UserIcon />,
    label: "Users",
    route: "/admin/users",
    permission: Permission.AppImpersonateUsers,
  },
  {
    icon: <Search />,
    label: "Provider Search",
    route: "/admin/provider-search",
    adminOnly: true,
  },
  {
    icon: <MessageCircle />,
    label: "All Chats",
    route: "/admin/chat",
    adminOnly: true,
  },
  {
    icon: <FilePlus2 />,
    label: "Job Posts",
    route: "/admin/job-posts",
    adminOnly: true,
  },
  {
    icon: <Building2 />,
    label: "Organizations",
    route: "/admin/organizations",
    adminOnly: true,
  },
  {
    icon: <Rss />,
    label: "Referrals",
    route: "/admin/referrals",
    adminOnly: true,
  },
  {
    icon: <Receipt />,
    label: "Usage Billing",
    route: "/admin/usage-billing",
    adminOnly: true,
  },
  {
    icon: <Landmark />,
    label: "Provider Payouts",
    route: "/admin/provider-payouts",
    permission: Permission.AppManageProviderPayouts,
  },
  {
    icon: <Users />,
    label: "Leads",
    route: "/admin/leads",
    permission: Permission.ViewLeads,
  },
];

export function AdminLayout({
  children,
  user,
  showSidebar = true,
}: {
  children: React.ReactNode;
  showSidebar?: boolean;
  user: User;
}) {
  const [openSheet, setOpenSheet] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();
  const { canAccessAdminSection, hasPermission } = usePermissions(user);

  // Check if user can access admin section at all
  if (!canAccessAdminSection) {
    return <AdminUnauthorized />;
  }

  // Filter sidebar items based on permissions
  const visibleSideItems = sideItems.filter((item) => {
    if (item.adminOnly) {
      return user.is_admin;
    }
    if (item.permission) {
      return hasPermission(item.permission);
    }
    return true;
  });

  const sidebarGridClass = collapsed
    ? "md:grid-cols-[72px_1fr] lg:grid-cols-[72px_1fr]"
    : "md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]";

  return (
    <div className={`min-h-dvh max-h-dvh  w-full grid ${sidebarGridClass}`}>
      <div className="hidden min-w-0 border-r border-slate-200 bg-white md:block">
        <TooltipProvider>
          <div className="flex h-full min-h-dvh max-h-dvh flex-col gap-2">
            <div
              className={
                collapsed
                  ? "flex h-14 shrink-0 items-center justify-center border-b border-slate-200 lg:h-[60px]"
                  : "flex h-14 shrink-0 items-center border-b border-slate-200 px-4 lg:h-[60px] lg:px-6"
              }
            >
              <div className="flex items-center gap-2 font-semibold">
                {collapsed ? (
                  <img
                    alt="Saile Logo"
                    src="/static/images/logo_icon.png"
                    className="size-8"
                  />
                ) : (
                  <img
                    alt="Saile Logo"
                    src="/static/images/logo.png"
                    className="w-[114px]"
                  />
                )}
              </div>
              {!collapsed && (
                <Button
                  variant="outline"
                  size="icon"
                  className="ml-auto mr-2 h-8 w-8"
                >
                  <Bell className="h-4 w-4" />
                  <span className="sr-only">Toggle notifications</span>
                </Button>
              )}
              {!collapsed && (
                <SidebarCollapseToggle
                  variant="header"
                  collapsed={collapsed}
                  onToggle={toggleCollapsed}
                />
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <nav
                aria-label="Main navigation"
                className="flex flex-col gap-1 px-3 py-4 text-sm"
              >
                {visibleSideItems.map((item) => (
                  <SidebarItem
                    key={item.label}
                    item={item}
                    variant="primary"
                    collapsed={collapsed}
                  />
                ))}
              </nav>
            </div>
            <SidebarCollapseToggle
              variant="footer"
              collapsed={collapsed}
              onToggle={toggleCollapsed}
            />
          </div>
        </TooltipProvider>
      </div>
      <div className="flex min-w-0 max-w-dvw flex-col">
        <header className="flex  items-center gap-4 border-b bg-muted py-2 px-4">
          {showSidebar && (
            <Sheet open={openSheet} onOpenChange={setOpenSheet}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0 md:hidden"
                >
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle navigation menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="flex h-auto flex-col rounded-r-2xl border-slate-200 bg-white px-4 pb-[max(1rem,var(--ion-safe-area-bottom,0px))]"
              >
                <SheetTitle className="sr-only">Admin Navigation</SheetTitle>
                <img
                  alt="Saile Logo"
                  src="/static/images/logo.png"
                  className="ml-2 w-[114px] shrink-0"
                />
                <nav
                  aria-label="Main navigation"
                  className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1 py-1"
                >
                  {visibleSideItems.map((item) => (
                    <SidebarItem
                      onClick={() => setOpenSheet(false)}
                      key={item.label}
                      item={item}
                      variant="primary"
                    />
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          )}
          <div className="w-full flex-1"></div>
          <UserAvatar user={user} />
        </header>
        <main className="min-w-0 max-w-dvw flex-1 overflow-auto bg-gray-100">
          {children}
        </main>
      </div>
    </div>
  );
}
